<script setup>
import { computed, reactive } from 'vue';
import Message from './Message.vue';
import ActivityCollapseGroup from './ActivityCollapseGroup.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: [Number, String],
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

const ACTIVITY_TYPE = MESSAGE_TYPES.ACTIVITY;
/** More than 6 consecutive activity rows → collapse to 3 + link + 3 */
const ACTIVITY_RUN_COLLAPSE_AT = 7;

const renderRows = computed(() => {
  const list = allMessages.value;
  const rows = [];
  let i = 0;
  while (i < list.length) {
    const msg = list[i];
    if (msg.messageType === ACTIVITY_TYPE) {
      let j = i + 1;
      while (j < list.length && list[j].messageType === ACTIVITY_TYPE) {
        j += 1;
      }
      const run = list.slice(i, j);
      if (run.length >= ACTIVITY_RUN_COLLAPSE_AT) {
        rows.push({
          kind: 'activityRun',
          messages: run,
          startIndex: i,
        });
      } else {
        for (let k = 0; k < run.length; k += 1) {
          rows.push({
            kind: 'message',
            message: run[k],
            index: i + k,
          });
        }
      }
      i = j;
    } else {
      rows.push({ kind: 'message', message: msg, index: i });
      i += 1;
    }
  }
  return rows;
});

const rowKey = row => {
  if (row.kind === 'message') return row.message.id;
  return `activity-run-${row.startIndex}-${row.messages[0]?.id ?? 'x'}`;
};
</script>

<template>
  <ul class="bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="row in renderRows" :key="rowKey(row)">
      <slot
        v-if="
          row.kind === 'message' &&
          firstUnreadId &&
          row.message.id === firstUnreadId
        "
        name="unreadBadge"
      />
      <Message
        v-if="row.kind === 'message'"
        v-bind="row.message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(row.message)"
        :group-with-next="shouldGroupWithNext(row.index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', row.message)"
      />
      <ActivityCollapseGroup
        v-else
        :messages="row.messages"
        :start-index="row.startIndex"
        :current-user-id="currentUserId"
        :is-email-inbox="isAnEmailChannel"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :get-in-reply-to-message="getInReplyToMessage"
        :should-group-with-next="shouldGroupWithNext"
        :all-messages="allMessages"
        @retry="emit('retry', $event)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
